import Link from 'next/link';
import { RecordInterface, Input } from '@components/common/Forms';
import {
  Dispatch, forwardRef, memo, SetStateAction, MouseEvent, useEffect, useRef, useState, useCallback, useMemo,
} from 'react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { ApplicantStatusEnum, OperationJob, StatusChangeActorEnum } from '@type/v1-api-types';
import { CardAnimations } from '@type/card';
import { useTranslation } from 'next-i18next';
import {
  Anchor, Avatar, Button, Card, ImageBj, Modal, Tooltip,
} from '@components/common';
import {
  ArrowPathIcon,
  ChatBubbleBottomCenterIcon,
  ArrowTopRightOnSquareIcon,
  StarIcon as StarIconSolid,
  HandThumbUpIcon,
  HandThumbDownIcon,
} from '@heroicons/react/24/solid';
import {
  BriefcaseIcon,
  EyeIcon,
  EyeSlashIcon,
  MapPinIcon,
  StarIcon as StarIconOutline,
} from '@heroicons/react/24/outline';
import { SendMessageModalContent } from '@components/templates/cv/partials/modals/SendMessageModalContent';
import { clsx } from 'clsx';
import { locationPreferencesKeys } from '@utils/resume/job-type-preferences';
import { dataTestId } from '@services/data-test-id';
import { buildSearchParams } from '@utils/data-fetching/searchParams';
import { convertToLocalizedDate } from '@utils/date-time/date-time';
import { BaseTrackingEventData, trackEvent } from '@services/tracking';
import { useAppSelector } from '@store/index';
import { getUserHeaderInfo } from '@store/reducers/userSelector';
import { useInView } from 'react-intersection-observer';
import { useUnblockOperations } from '@components/templates/cv/hooks/useUnblockOperations';
import { CustomConfirmModalContent } from '@components/templates/cv/partials/modals/CustomConfirmModalContent';

import { getI18nLinkHref } from '@services/i18nPaths';
import type { McApplicantCardData } from '@components/templates/employer/applicant-list/flow-mc/types';
import {
  ApplicantBadgePill, ApplicantInterest,
} from './partials';
import { useMcTalentCardActions } from './hooks/useMcTalentCardActions';

// Interface
// ******************************************
export interface McTalentCardFormProps {
  record: RecordInterface,
  name: string,
  setSelectAll?: Dispatch<SetStateAction<boolean | undefined>>,
  getSelectedApplicants?: () => string[],
}

interface McTalentCardProps {
  cardData: McApplicantCardData & { hideStatus?: boolean; },
  locale: Locale,
  className?: string,
  columnName?: string,
  statusContext?: string | number,
  animation?: CardAnimations,
  disableFolderStatus?: boolean,
  handleRowUnchecked?: (columnName: string) => void;
  trackingData? : BaseTrackingEventData;
  internalAgent?: boolean,
  showActions?: boolean,
  revalidatePage?: (userSlug: string) => void,
  jobSlug?: string,
  jobInfo?: ApiSchema<'ListReadJob'>;
  applicant?: McTalentCardFormProps,
  openTalentModal?: (userSlug: string, as?: string) => void,
  openPhoneApplicantPreview?: (applicant: McApplicantCardData) => void,
  trackingId?: string,
  isPassiveRecommendation?: boolean | 0 | 1,
  handleStatusChangeOnCard?: (statusId: string | number, cvSlug: string) => void,
  statusDropdownOptions?: { id: string; label: string; statusId: number; className: string }[],
  onCardStatusChange?: (statusId: number, userSlug: string) => void,
  mode?: 'default' | 'alumni',
  onView?: (talentSlug: string) => void,
}


/**
 * @description TalentCard component. To be used in the TalentSearch page.
 *
 * - cardData: *ApiUserTalentItem* - The data for the card. Required.
 * - locale - *Locale* - The current app locale.
 * - isCompany: *boolean* - Check whether the authenticated user is company or not. Optional.
 * - className: *string* - Additional classes for the card. Optional.
 * - animation: *CardAnimations* - The animation for the card. Optional. Default is 'rise'.
 * - internalAgent: *boolean* - If true, the card will have a special button. Optional. Default is undefined.
 * - jobSlug: *string* - The job slug. Optional. Default is undefined. Needed for the CV opening in the job context.
 * - jobInfo: The job information.
 * - applicant: *object* - The object containing the record function and the name of the applicant. Optional. Default is undefined.
 * - openTalentModal: *function* - If provided, the CV will open in a modal. Optional.
 * - revalidatePage: *function* - The function to revalidate the page. Optional. Default is undefined.
 * - showActions: *boolean* - If true, the card will show the actions. Optional. Default is true.
 * - trackingId: *string* - The request id from the page in the list (each page has a different apiRequestId string). Used for tracking.
 * - trackingData: *string* - The object for getting tracking details from original list
 * - isPassiveRecommendation: *boolean* - If true, we don't count towards the Cv view limit. Optional. Default is false.
 * - handleStatusChangeOnCard: *function* - Function to handle the status change on the card. Optional. Default is undefined.
 * - mode: *'default' | 'alumni'* - The mode of the card. Default is 'default'.
 *
 * Applicant object:
 * - applicant.record: *RecordInterface* - The record function from useBjForm. Required.
 * - applicant.name: *string* - The name of the form group. Required.
 * - applicant.setSelectAll: *Dispatch<SetStateAction<boolean | undefined>>* - The function to set the 'select all' checkbox. Optional. Default is undefined.
 */
export const McTalentCard = forwardRef<HTMLDivElement, McTalentCardProps>((props, ref) => {
  // Destructure props
  const {
    cardData,
    locale,
    disableFolderStatus = false,
    className,
    animation = 'rise',
    internalAgent,
    showActions = true,
    revalidatePage,
    jobSlug,
    jobInfo,
    applicant,
    trackingData,
    openTalentModal,
    openPhoneApplicantPreview,
    trackingId,
    handleRowUnchecked,
    columnName,
    statusContext,
    isPassiveRecommendation = false,
    handleStatusChangeOnCard,
    statusDropdownOptions,
    onCardStatusChange,
    mode = 'default',
    onView,
  } = props;

  const {
    record,
    name,
    setSelectAll,
    getSelectedApplicants,
  } = applicant || {};

  // Translation
  const { t } = useTranslation('common');


  // Helper variables
  // *****************************************
  const leadExperience = cardData?.leadExperience?.title?.trim() || undefined;
  const talentName = cardData?.privateInformation?.name?.trim() || undefined;
  const talentSlug = cardData?.slug ?? '';
  const hasTalentSlug = Boolean(talentSlug);
  const mcProfileIdentifierType = cardData?.mcProfileIdentifierType ?? 'other';
  const canOpenCvDetail = mcProfileIdentifierType === 'user' && hasTalentSlug;
  const canSelectApplicant = mcProfileIdentifierType === 'user' && hasTalentSlug;
  const canOpenPhoneApplicantPreview = mcProfileIdentifierType === 'phone' && Boolean(openPhoneApplicantPreview);
  const talentCity = (cardData?.location)?.split(', ')[0];
  const talentPreferences = cardData?.preferences?.filter((item) => item !== t(locationPreferencesKeys.relocation))?.join(', ') || '';
  const availableForRelocation = cardData?.preferences?.some((item) => item === t(locationPreferencesKeys.relocation));
  const hasVideoCv = !!cardData?.privateInformation?.videoUrl;
  const mcProcessingStatus = cardData?.mcProcessingStatus ?? null;
  const mcEvaluationStatus = typeof cardData?.mcEvaluationStatus === 'number'
    ? cardData.mcEvaluationStatus
    : undefined;
  const mcSummary = cardData?.mcSummary?.trim();
  const mcFinalScore = typeof cardData?.mcFinalScore === 'number' && Number.isFinite(cardData.mcFinalScore)
    ? cardData.mcFinalScore
    : undefined;
  const hasMcFinalScore = typeof mcFinalScore === 'number' && mcFinalScore >= 0;
  const normalizedMcFinalScore = hasMcFinalScore
    ? Math.max(0, Math.min(100, Math.round(mcFinalScore)))
    : undefined;
  const isMcProcessing = mcProcessingStatus === 'PROCESSING';
  const isMcInterviewProcessing = mcProcessingStatus === 'INTERVIEW_PROCESSING';
  const shouldShowPhonePlaceholder = !talentName && !leadExperience && Boolean(mcProcessingStatus);
  const displayCardTitle = talentName || leadExperience || (
    shouldShowPhonePlaceholder ? t('mc.applicant.phone_placeholder') : undefined
  );
  const cardOverlayLabel = displayCardTitle || cardData?.mcPhoneIdentifierValue || t('mc.applicant.phone_placeholder');
  const displayMcSummary = isMcProcessing
    ? t('mc.applicant.evaluation_in_progress')
    : mcSummary;
  let displayMcMatchValue: string | undefined;
  if (isMcProcessing) {
    displayMcMatchValue = t('mc.applicant.upload_in_progress');
  } else if (typeof normalizedMcFinalScore === 'number') {
    displayMcMatchValue = `${normalizedMcFinalScore}%`;
  }
  const showMcMatchBlock = Boolean(displayMcMatchValue || displayMcSummary || isMcInterviewProcessing);
  const hasTrackedView = useRef<boolean>(false);

  // Prevent duplicate flags
  const spokenLanguages = cardData?.spokenLanguages
    ? [...new Map(cardData.spokenLanguages.map((lang) => [lang.shortName, lang])).values()]
    : undefined;

  // Add query params to the card link. Just make them optional object keys, don't pollute the URL if not needed.
  const cardLinkUrlParams = buildSearchParams({
    ...(trackingId ? { rid: trackingId } : {}),
    ...(isPassiveRecommendation ? { isPassiveRecommendation: 1 } : {}),
  });


  const isAddedToShortlist = (!jobSlug && cardData?.interviewFolder?.some((item) => (item.statusId as ApplicantStatusEnum) === ApplicantStatusEnum.SHORTLIST))
    || (jobSlug && (cardData.application?.statusId as ApplicantStatusEnum) === ApplicantStatusEnum.SHORTLIST);

  // Common classes
  // *****************************************
  const cardClasses = clsx(
    'group/talentCard relative overflow-hidden rounded-md !border-none bg-surface !shadow-none',
    className,
    {
      'border-y-2 border-x border-pink': cardData?.hasUserSubscription,
      'shadow-xs': cardData?.application?.isNew,
    },
  );
  const titleClasses = 'mt-1 mb-2.5 font-bold leading-tight text-lg';
  const cvCompletionBadgeClasses = '!bg-secondary z-2 text-white !outline-surface !ring-surface !bottom-auto top-0 !-left-1';
  const cardFooterClasses = 'relative z-2 can-hover:h-0 can-hover:transition-all can-hover:duration-200 can-hover:opacity-0 group-hover/talentCard:opacity-100 group-hover/talentCard:-translate-y-13';
  const cardActionWrapperClasses = `h-13 bg-surface-50 grid items-center divide-x divide-input ${disableFolderStatus ? 'grid-cols-1' : 'grid-cols-2'}`;
  const cardActionButtonClasses = 'group hover:!no-underline hover:enabled:text-ink focus-visible:text-ink';
  const cardActionButtonIconClasses = 'w-5 h-5 mr-2';
  const cardActionButtonIconSecondaryClasses = 'transition-all duration-200 text-primary group-hover:group-enabled:text-primary';


  const headerInfoData = useAppSelector(getUserHeaderInfo);
  const userId = headerInfoData?.userId;
  const isAuthenticated = !!headerInfoData || !!jobSlug;

  let cardLink = getI18nLinkHref(`/cv/${talentSlug}${cardLinkUrlParams}`, locale);
  if (jobSlug) {
    cardLink = getI18nLinkHref(`/mc/job/${jobSlug}/cv/${talentSlug}${cardLinkUrlParams}`, locale);
  }

  // Handle the CV opening (normal or in a modal, depending on the props)
  // If we have a jobSlug, we open the CV in the job context
  // *****************************************
  const handleCvOpening = (e: MouseEvent<HTMLAnchorElement>) => {
    if (openTalentModal) {
      e.preventDefault();
      openTalentModal(talentSlug, cardLink);
    } else if (!isAuthenticated) {
      window.location.href = '/login';
    }
  };

  const handlePhoneApplicantPreviewOpening = () => {
    openPhoneApplicantPreview?.(cardData);
  };


  const resolveEvaluationStatus = (
    evaluationStatus?: number | null,
    score?: number | null,
  ) => {
    if (evaluationStatus === 1 || evaluationStatus === 2 || evaluationStatus === 3) {
      return evaluationStatus;
    }
    if (typeof score !== 'number' || score <= 0) {
      return undefined;
    }
    if (score <= 50) {
      return 3;
    }
    if (score <= 69) {
      return 2;
    }
    return 1;
  };

  const resolvedMcEvaluationStatus = resolveEvaluationStatus(mcEvaluationStatus, normalizedMcFinalScore);

  const renderIconByEvaluationStatus = (evaluationStatus?: number) => {
    if (typeof evaluationStatus !== 'number') {
      return null;
    }
    if (evaluationStatus === 1) {
      return <HandThumbUpIcon className="size-5 min-w-5 text-secondary" />;
    }
    if (evaluationStatus === 2) {
      return <HandThumbUpIcon className="size-5 min-w-5 text-warning" />;
    }
    return <HandThumbDownIcon className="size-5 min-w-5 text-error" />;
  };

  const getScoreTextClass = (evaluationStatus?: number) => {
    if (typeof evaluationStatus !== 'number') {
      return 'text-ink-light';
    }
    if (evaluationStatus === 1) {
      return 'text-secondary';
    }
    if (evaluationStatus === 2) {
      return 'text-warning';
    }
    return 'text-error';
  };

  const [activeJobContext, setActiveJobContext] = useState<OperationJob>({
    jobId: Number(jobInfo?.jobId),
    title: jobInfo?.title ?? '',
    jobSlug: jobSlug ?? '',
  });
  const parsedResumeIdToUnlock = Number(cardData.id);
  const resumeIdToUnlock = Number.isInteger(parsedResumeIdToUnlock) && parsedResumeIdToUnlock > 0
    ? parsedResumeIdToUnlock
    : undefined;

  // Profile operations
  // ***********************************************
  const {
    displaySendMessageModal,
    isLoadingSendMessage,
    toggleSendMessageModal,
    isLoadingOperationJob,
    saveToShortlistAction,
    openMessageWithJobFolder,
    shouldOpenMessageModal,
  } = useMcTalentCardActions({
    jobSlug: activeJobContext?.jobSlug,
    jobFolderId: activeJobContext?.jobId,
    jobTitle: activeJobContext?.title,
    userSlug: talentSlug,
    locale: locale ?? 'ro',
    revalidatePage: revalidatePage ?? (() => {}),
    hasInterviewFolder: !!cardData?.hasInterviewFolder,
    interviewFolders: cardData?.interviewFolder,
    rid: trackingId,
    handleStatusChangeOnCard,
    isPassiveRecommendation: Boolean(isPassiveRecommendation),
  });

  const {
    openPricingInformationModal,
    confirmModalOpen,
    unblockCv,
    cancelUnlock,
    hasSkipUnlockConfirm,
  } = useUnblockOperations({
    userIdToUnlock: resumeIdToUnlock,
    jobSlug: activeJobContext?.jobSlug,
    jobTitle: activeJobContext?.title,
    jobFolderId: Number(activeJobContext?.jobId),
  });

  const { ref: inViewRef, inView } = useInView({ threshold: 1, triggerOnce: true });

  // Merge forwarded ref with inViewRef
  const mergedRef = useCallback((node: HTMLDivElement | null) => {
    inViewRef(node);
    if (typeof ref === 'function') {
      ref(node);
    } else if (ref) {
      ref.current = node;
    }
  }, [ref, inViewRef]);

  // Added tracking seen event
  useEffect(() => {
    if (trackingData?.disableTracking) return;
    if (!talentSlug) return;
    if (inView && !hasTrackedView.current) {
      trackEvent({
        eventType: 'view',
        entityType: 'resume',
        entityId: talentSlug,
        viewContext: 'card',
        ...(userId && { userId: userId.toString() }),
        ...(trackingData?.listUuid && { listUuid: trackingData.listUuid }),
        ...(trackingData?.listType && { listType: trackingData.listType }),
        ...(trackingData?.position && { position: trackingData.position }),
        metadata: [
          ...(trackingData?.columnName ? [{ key: 'columnName', value: trackingData.columnName }] : []),
          ...(jobSlug ? [{ key: 'jobSlug', value: jobSlug }] : []),
        ],
      });
      onView?.(talentSlug);
      hasTrackedView.current = true;
    }
  }, [inView, talentSlug, userId, trackingData, onView, jobSlug]);

  const continueUnlockProgress = () => {
    trackEvent({
      eventType: 'action',
      entityType: 'resume',
      entityId: talentSlug,
      viewContext: 'card',
      action: 'contact',
      ...(trackingData?.listType && { listType: trackingData?.listType }),
      ...(trackingData?.position && { position: trackingData?.position }),
      ...(userId && { userId: userId.toString() }),
      metadata: [
        ...(trackingData?.columnName ? [{ key: 'columnName', value: trackingData.columnName }] : []),
        ...(jobSlug ? [{ key: 'jobSlug', value: jobSlug }] : []),
      ],
    });
    if (!resumeIdToUnlock) {
      openMessageWithJobFolder();
      return;
    }
    void unblockCv({
      type: 'message',
      onSuccess: () => {
        toggleSendMessageModal();
        if (cardData?.locked && talentSlug) {
          revalidatePage?.(talentSlug);
        }
      },
    });
  };


  // Render component
  // *****************************************
  return (
    <Card ref={mergedRef} animation={animation} className={cardClasses} {...dataTestId('cvSearchPage.card.default')}>
      <Card.Body className="relative flex gap-4 px-4 py-6">
        {/* CARD LINK */
            canOpenCvDetail && (
              <Link
                href={cardLink}
                prefetch={false}
                draggable={false}
                onClick={(e) => {
                  trackEvent({
                    eventType: 'click',
                    entityType: 'resume',
                    entityId: talentSlug,
                    viewContext: 'card',
                    ...(trackingData?.listType && { listType: trackingData.listType }),
                    ...(trackingData?.position && { position: trackingData.position }),
                    ...(trackingData?.listUuid && { listUuid: trackingData.listUuid }),
                    ...(userId && { userId: userId.toString() }),
                    metadata: [
                      ...(trackingData?.columnName ? [{ key: 'columnName', value: trackingData.columnName }] : []),
                      ...(jobSlug ? [{ key: 'jobSlug', value: jobSlug }] : []),
                    ],

                  });
                  handleCvOpening(e);
                }}
                className="absolute inset-0 z-1 flex"
              >
                {/* Tooltip for premium users on the talent list (no job context) */
                  cardData?.hasUserSubscription && !jobSlug ? (
                    <Tooltip
                      tooltipText={t('premium.cv.badge.tooltip')}
                      position="top"
                      floating
                      className="block size-full"
                      triggerContainerClass="block w-full h-full"
                      colorScheme="pink"
                    >
                      <span className="opacity-0">
                        {leadExperience || talentName}
                      </span>
                    </Tooltip>
                  ) : (
                    <span className="opacity-0">
                      {leadExperience || talentName}
                    </span>
                  )
                }
              </Link>
            )
          }

        {!canOpenCvDetail && canOpenPhoneApplicantPreview && (
          <div
            role="button"
            tabIndex={0}
            onClick={handlePhoneApplicantPreviewOpening}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handlePhoneApplicantPreviewOpening(); } }}
            className="absolute inset-0 z-1 flex cursor-pointer"
            aria-label={cardOverlayLabel}
            title={cardData?.viewedAt ? convertToLocalizedDate(cardData.viewedAt, locale) : cardOverlayLabel}
          >
            <span className="opacity-0">{cardOverlayLabel}</span>
          </div>
        )}


        {/* APPLICANT checkbox; uncheck the 'select all' checkbox on click */
            record && name && canSelectApplicant && (
              <label
                htmlFor="applicantSlug"
                aria-label={t('global.default.select.option')}
                title={t('global.default.select.option')}
                className="absolute -top-1 right-0 z-2 cursor-copy p-1 transition-opacity can-hover:pointer-events-none can-hover:opacity-0 can-hover:focus-within:pointer-events-auto can-hover:focus-within:opacity-100 can-hover:group-hover/talentCard:pointer-events-auto can-hover:group-hover/talentCard:opacity-100 can-hover:has-[:checked]:pointer-events-auto can-hover:has-[:checked]:opacity-100"
              >
                <Input
                  defaultValue={talentSlug}
                  type="checkbox"
                  className="cursor-copy text-secondary ring-3 ring-surface checked:bg-secondary"
                  {...record(name, {
                    id: `${name}_${talentSlug}`,
                    valueAsArray: true,
                    columnName,
                  })}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    if (setSelectAll) setSelectAll(undefined);
                    if (getSelectedApplicants) getSelectedApplicants();
                    if (!e.target.checked && columnName) {
                      handleRowUnchecked?.(columnName);
                    }
                  }}
                />
              </label>
            )
          }


        {/* DESCRIPTION */}
        <div className="min-w-0 grow">
          {(internalAgent || cardData?.activityStatus || cardData?.interest) && (
          <div className="mb-2 flex min-w-0 items-start gap-3 divide-x divide-ink/15 text-sm text-ink-light">
            {/* Activity status; hide it in applicants' lists */
                  !jobSlug && cardData?.activityStatus && (
                    <span className="flex shrink-0 items-center gap-2">
                      <span className="inline-block size-2.5 rounded-full bg-secondary" />
                      {cardData?.activityStatus}
                    </span>
                  )
                }

            {/* Interest (i.e.: applied at date); show only in the job context */
                  jobSlug && cardData?.interest && (
                    <ApplicantInterest
                      type={cardData.interest?.type}
                      value={cardData.interest?.value}
                      locale={locale}
                    />
                  )
                }

            {/* Internal agent button */
                  internalAgent && cardData?.id && (
                    <span {...cardData?.activityStatus || cardData?.interest ? { className: 'pl-3' } : {}}>
                      <Anchor
                        href={`https://intranet.bestjobs.eu/user/detail/${cardData?.id}`}
                        styling="text"
                        size="sm"
                        color="primary-light"
                        rounding="none"
                        aria-label="internal-agent"
                        title="internal-agent"
                        className="relative z-2"
                        target="_blank"
                      >
                        <ArrowTopRightOnSquareIcon className="size-4.5" />
                      </Anchor>
                    </span>
                  )
                }

            {cardData?.application && (
              cardData.application.isNew ? (
                <EyeSlashIcon className="size-4 !border-0 text-ink-light" />
              ) : (
                <EyeIcon className="size-4 !border-0 text-secondary" />
              )
            )}
          </div>
          )}

          {/* The main title of the card: Lead experience or name (if CV is unlocked) */
              displayCardTitle && (
                <div className={titleClasses}>{displayCardTitle}</div>
              )
            }

          {/* The rest of the description */}
          <div className="text-sm text-ink-medium">
            {/* Lead experience if CV unlocked - that means the title is the name */
                talentName && leadExperience && (
                  <div className="font-bold">{leadExperience}</div>
                )
              }
            {/* Lead experience company: hidden when alumni */
                mode === 'default' && cardData?.leadExperience?.company && (
                  <div>{`@ ${cardData?.leadExperience?.company}`}</div>
                )
              }
            {/* Lead experience period */
                cardData?.leadExperience?.period && (
                  <div>
                    {mode === 'default' && `${t('card.experience.total.domain')} `}
                    <strong>{cardData?.leadExperience?.period}</strong>
                  </div>
                )
              }
            {
                mode === 'alumni' && cardData?.experienceSummary && (
                  <div className="mb-0.5 mt-2.5 flex gap-1 text-xs">
                    <BriefcaseIcon className="mt-px size-4 shrink-0" />
                    <span>{`${t('cv.section.label.professional_experience')}: ${cardData?.experienceSummary}`}</span>
                  </div>
                )
              }
            {/* Location (city only) and preferences */
                (talentCity || talentPreferences) && (
                  <div className="mb-0.5 mt-2.5 flex gap-1 text-xs">
                    <MapPinIcon className="mt-px size-4 shrink-0" />
                    <span className="line-clamp-2 grow">
                      {talentCity}
                      {/* Location preferences */}
                      {mode === 'default' && (
                        <>
                          {availableForRelocation && (
                            <>
                              {', '}
                              <strong>
                                {t(locationPreferencesKeys.relocation)}
                              </strong>
                            </>
                          )}
                          {talentPreferences && ', '}
                          {talentPreferences}
                        </>
                      )}
                    </span>
                  </div>
                )
              }

          </div>
        </div>

        {/* AVATAR - make room for buck checkbox when in job context */}
        <div className="shrink-0">
          {/* Talent avatar */}
          <div className="relative z-2">
            {/* Badge for 100% Cv completion */
                cardData?.cvCompletion === 100 && (
                  <Avatar.Badge withRing iconName="star" className={cvCompletionBadgeClasses} />
                )
              }

            {(isMcProcessing || isMcInterviewProcessing) ? (
              <div className="flex size-16 items-center justify-center rounded-full bg-slate-100">
                <ArrowPathIcon className="size-6 animate-spin text-slate-400" />
              </div>
            ) : (
              <Avatar
                imgPath={cardData?.imgUrl}
                imgFolder="resume_image"
                extractImageName
                className={`mx-auto ${hasVideoCv ? '!outline-2 !outline-offset-3 !outline-secondary' : ''}`}
                alt={talentName}
                name={talentName}
                withRing={hasVideoCv}
              />
            )}

          </div>

          {/* Language flags */
              spokenLanguages && spokenLanguages.length && (
                <div className="mt-3 flex items-center justify-center gap-1.5">
                  {spokenLanguages.slice(0, 2).map((lang) => (
                    <ImageBj
                      key={`${lang.shortName}-${cardData.slug}-${cardData?.id}`}
                      src={lang.flagUrl || ''}
                      alt=""
                      width={16}
                      height={16}
                      className="size-4 shrink-0"
                    />
                  ))}
                  {/* Show if we have more than 2 languages */
                    spokenLanguages.length > 2 && (
                      <span className="text-xs text-ink-medium">{`+${spokenLanguages.length - 2}`}</span>
                    )
                  }
                </div>
              )
            }

          {/* Application status */
              cardData?.application && (
                <div className="mt-2 text-center">
                  {!cardData?.hideStatus && (
                  <ApplicantBadgePill
                    statusId={cardData.application?.statusId}
                    statusChangeActor={cardData.application?.statusChangeActor as StatusChangeActorEnum | undefined}
                    statusContext={statusContext ?? columnName}
                  />
                  )}
                </div>
              )
            }
        </div>

      </Card.Body>

      {/* MC match and summary use a full-width block below the body, same placement as non-MC evaluation */}
      {
        mode === 'default' && showMcMatchBlock && (
          <div className="px-3 pb-3">
            <div className="rounded-md bg-surface-50 p-2">
              {displayMcMatchValue && (
                <div className="flex items-center gap-1.5 text-xs leading-4">
                  <span className="font-medium text-ink">Match:</span>
                  <span
                    className={clsx(
                      'font-semibold',
                      isMcProcessing ? 'text-ink-light' : getScoreTextClass(resolvedMcEvaluationStatus),
                    )}
                  >
                    {displayMcMatchValue}
                  </span>
                  {!isMcProcessing
                    && typeof normalizedMcFinalScore === 'number'
                    && normalizedMcFinalScore > 0
                    && renderIconByEvaluationStatus(resolvedMcEvaluationStatus)}
                </div>
              )}

              {isMcProcessing && (
                <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-slate-200">
                  <div className="h-full w-1/2 animate-pulse rounded-full bg-secondary" />
                </div>
              )}

              {isMcInterviewProcessing && (
                <div className="mt-0.5 text-xs italic text-ink-light">
                  {t('mc.applicant.partial_match_increasing')}
                </div>
              )}

              {displayMcSummary && (
                <div className="mt-1 line-clamp-3 break-words text-tiny font-thin">
                  {displayMcSummary}
                </div>
              )}
            </div>
          </div>
        )
      }

      {/* STATUS DROPDOWN */}
      {statusDropdownOptions && statusDropdownOptions.length > 0 && onCardStatusChange && talentSlug && (
        <McCardStatusDropdown
          options={statusDropdownOptions}
          currentStatusId={cardData.application?.statusId as number | undefined}
          onSelect={(statusId) => onCardStatusChange(statusId, talentSlug)}
          t={t}
        />
      )}

      {/* CARD ACTIONS */}
      {
          (showActions) && (
            <Card.Footer className={cardFooterClasses}>
              <div className={cardActionWrapperClasses}>
                {/* Save to shortlist */}

                {!disableFolderStatus
                  && (
                  <Button
                    styling="text"
                    size="sm"
                    color="ink-medium"
                    rounding="none"
                    className={cardActionButtonClasses}
                    isLoading={isLoadingOperationJob && !shouldOpenMessageModal}
                    disabled={!hasTalentSlug}
                    onClick={() => {
                      trackEvent({
                        eventType: 'action',
                        entityType: 'resume',
                        entityId: talentSlug,
                        viewContext: 'card',
                        action: 'save',
                        ...(trackingData?.listType && { listType: trackingData?.listType }),
                        ...(trackingData?.position && { position: trackingData?.position }),
                        ...(trackingData?.listUuid && { listUuid: trackingData?.listUuid }),
                        ...(userId && { userId: userId.toString() }),
                      });
                      saveToShortlistAction();
                    }}
                    {...dataTestId('cvSearchPage.card.saveToShortlistAction')}
                  >
                    {!(isLoadingOperationJob && !shouldOpenMessageModal) && (
                      isAddedToShortlist
                        ? (
                          <StarIconSolid
                            className={clsx(cardActionButtonIconClasses, cardActionButtonIconSecondaryClasses)}
                          />
                        )
                        : (
                          <StarIconOutline
                            className={clsx(cardActionButtonIconClasses, cardActionButtonIconSecondaryClasses, 'stroke-2')}
                          />
                        )
                    )}
                    <span>{t('talent-card.v2.action.shortlist')}</span>
                  </Button>
                  )}


                {/* Send Message action */}
                <Button
                  styling="text"
                  size="sm"
                  color="ink-medium"
                  rounding="none"
                  className={cardActionButtonClasses}
                  isLoading={isLoadingSendMessage || (isLoadingOperationJob && shouldOpenMessageModal)}
                  disabled={isLoadingSendMessage || !hasTalentSlug}
                  onClick={() => {
                    if (cardData?.locked) {
                      openPricingInformationModal();
                    } else if (hasSkipUnlockConfirm()) {
                      continueUnlockProgress();
                    } else {
                      continueUnlockProgress();
                    }
                  }}
                  {...dataTestId('cvSearchPage.card.unlockWithMessage')}
                >
                  {!(isLoadingSendMessage || (isLoadingOperationJob && shouldOpenMessageModal)) && (
                    <ChatBubbleBottomCenterIcon className={cardActionButtonIconClasses} />
                  )}
                  <span>
                    {t('talent-card.v2.action.message')}
                    {' '}
                  </span>
                </Button>

              </div>
            </Card.Footer>
          )
      }

      {/* Send message modal */}
      {cardData && hasTalentSlug && (
        <>
          <Modal
            isOpen={displaySendMessageModal}
            body={(
              <SendMessageModalContent
                imageUrl={cardData.imgUrl || ''}
                candidateName={cardData?.privateInformation?.name || ''}
                onSendSuccess={() => toggleSendMessageModal()}
                onClose={() => {
                  toggleSendMessageModal();
                }}
                slug={talentSlug}
                trackingId={trackingId}
                locale={locale}
              />
            )}
            onClose={() => {
              toggleSendMessageModal();
            }}
          />

          <Modal
            size="sm"
            isOpen={confirmModalOpen}
            body={(
              <CustomConfirmModalContent
                jobContextDetail={{
                  jobFolderId: activeJobContext.jobId,
                  jobSlug: activeJobContext.jobSlug,
                  title: activeJobContext.title,
                }}
                isPassiveRecommendation={Boolean(isPassiveRecommendation)}
                userSlug={talentSlug}
                updateActiveJobFolderContext={(item) => {
                  setActiveJobContext(item);
                }}
                jobSlug={jobSlug}
                onSendSuccess={(val) => {
                  if (val) {
                    continueUnlockProgress();
                    cancelUnlock();
                  } else {
                    cancelUnlock();
                  }
                }}
              />
            )}
            onClose={() => null}
          />
        </>

      )}


    </Card>
  );
});


/** @description Dropdown for changing card status in input-flow. */
const McCardStatusDropdown = ({ options, currentStatusId, onSelect, t }: {
  options: { id: string; label: string; statusId: number; className: string }[];
  currentStatusId?: number;
  onSelect: (statusId: number) => void;
  t: (key: string) => string;
}) => {
  // VIEWED (2) maps to the same "unprocessed" option as NEW (1)
  const normalizedStatusId = currentStatusId === 2 ? 1 : currentStatusId;
  const currentOption = options.find((o) => o.statusId === normalizedStatusId);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (e: Event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <div ref={dropdownRef} className="relative z-3 px-3 pb-2">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); setIsOpen((prev) => !prev); }}
        className="flex w-full items-center justify-between rounded-md border border-input/50 bg-surface-50 px-2.5 py-1.5 text-xs font-medium text-ink-medium transition hover:border-secondary/40 hover:text-ink"
      >
        <span className={currentOption?.className}>{currentOption && t(currentOption.label)}</span>
        <ChevronDownIcon className={clsx('size-4 transition-transform', isOpen && 'rotate-180')} />
      </button>
      {isOpen && (
        <div className="absolute inset-x-3 bottom-full z-10 mb-1 overflow-hidden rounded-md border border-input/30 bg-surface shadow-lg">
          {options.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onSelect(option.statusId);
                setIsOpen(false);
              }}
              className={clsx(
                'flex w-full items-center gap-2 px-3 py-2 text-xs font-medium transition hover:bg-surface-50',
                option.className,
              )}
            >
              {t(option.label)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/**
      * @description Memoized TalentCard component. To be used in the infinite lists.
      */
export const McTalentCardMemo = memo(McTalentCard);
